import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/components/ui/use-toast";
import { baseCustomersQueryKey } from "@/lib/api/customer";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { RecordFormSkeleton } from "../form/components/RecordFormPageLayout";
import { RecordForm } from "../form/RecordForm";
import { useRecordEditor, type RecordEditorOptions } from "../form/useRecordEditor";
import { CrmDialogLoadError } from "./CrmDialogLoadError";

/**
 * Edits the CRM record linked to a customer, in a dialog. Saving syncs the customer row (same CRM write),
 * so the nx customer lists are reloaded. Without `onSaved`, a save shows "Updated" and closes the dialog.
 * Mount it only while open.
 */
export function CrmCustomerEditDialog({
  customerId,
  onClose,
  onSaved,
  prepareValues,
  validate,
}: {
  customerId: string;
  onClose: () => void;
  onSaved?: RecordEditorOptions["onSaved"];
  prepareValues?: RecordEditorOptions["prepareValues"];
  validate?: RecordEditorOptions["validate"];
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const editor = useRecordEditor({
    target: { customer_id: customerId },
    onSaved: (result) => {
      void queryClient.invalidateQueries({ queryKey: baseCustomersQueryKey });
      if (onSaved) {
        onSaved(result);
        return;
      }
      toast({ title: "Updated", description: "" });
      onClose();
    },
    prepareValues,
    validate,
  });

  return (
    <Dialog open onOpenChange={(nextOpen: boolean) => { if (!nextOpen) onClose(); }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg md:max-w-3xl lg:max-w-5xl" data-testid="crm-customer-edit-dialog">
        <DialogHeader>
          <DialogTitle>{editor.status === "open" ? `Edit ${editor.record.long_name}` : "Edit customer"}</DialogTitle>
          <DialogDescription>
            {editor.status === "open" ? `Record Group: ${editor.master.long_name}` : "The customer's CRM record."}
          </DialogDescription>
        </DialogHeader>
        {editor.status === "loading" && <RecordFormSkeleton />}
        {editor.status === "error" && (
          <CrmDialogLoadError subject="customer's CRM record" isRetrying={editor.isRetrying} onRetry={editor.retry} />
        )}
        {editor.status === "open" && (
          <RecordForm
            key={editor.version}
            mode="update"
            {...editor.form}
            lockedFields={{ master_id: true }}
            onCancel={onClose}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
